import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { getDisplayCustomFieldsValues } from 'modules/subassignmentManagement/components/SubassignmentTable/store/selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { useStyles } from 'shared/components/auth/AuthStyles';
import FormField from 'shared/components/formFields/FormField';
import FormSelect from 'shared/components/selects/FormSelect';
import { useSheetRejectStyles } from 'shared/components/sidebars/SheetReject/styles';
import { useSidebarStyles } from 'shared/components/sidebars/Sidebar/styles';
import { PayRateType } from 'shared/models/PaySettings';
import { formatDollars } from 'shared/utils/formatters/dollarFormatter';
import {
    selectCustomFieldValuesByIds,
    selectOrderedOfferLetterCustomFields,
} from 'store/entities/customFields/selectors';
import { IOfferLetter, RescissionReasons } from '../../store/model';
import { getOfferLabel } from '../../store/utils';

interface IOfferLetterRescissionPanelProps {
    offerLetter: IOfferLetter;
    hasCustomReason: boolean;
}

const getOfferLetterPayRateLabel = (offerLetter: IOfferLetter): string => {
    const payRateTypeLabel = (() => {
        switch (offerLetter.pay_rate_type) {
            case PayRateType.DAILY:
                return 'day';
            case PayRateType.PER_HOUR:
                return 'hr';
            case PayRateType.PER_WEEK:
                return 'week';
            case PayRateType.MONTHLY:
                return 'month';
            case PayRateType.SEMI_MONTHLY:
                return 'semi month';
            case PayRateType.BI_WEEKLY:
                return 'bi week';
        }
    })();
    return `${formatDollars(offerLetter.pay_rate_value)}/${payRateTypeLabel}`;
};

/**
 * @todo Need to be produced as shared component with src/shared/components/sidebars/SheetReject/SheetRejectNotes.tsx
 */
export default function OfferLetterRescissionPanel({ offerLetter, hasCustomReason }: IOfferLetterRescissionPanelProps) {
    const classes = useSheetRejectStyles();
    const sidebarClasses = useSidebarStyles();
    const inputClasses = useStyles();
    const offerLetterFields = useSelector(selectOrderedOfferLetterCustomFields);
    const customFieldValuesByIds = useSelector(selectCustomFieldValuesByIds);

    const displayCustomFieldValues = getDisplayCustomFieldsValues(
        offerLetterFields,
        customFieldValuesByIds,
        offerLetter.custom_field_value_ids ?? [],
    );

    return (
        <section className={clsx(sidebarClasses.sidebarElement, classes.rejectionCard)}>
            <Typography variant="h6" className={classes.nameOverflowed}>{getOfferLabel(offerLetter)}</Typography>
            <Box display="flex" justifyContent="space-between">
                <Box>
                    {offerLetterFields.map(field => (
                        <Typography variant="body2" key={field.id}>
                            {displayCustomFieldValues[field.id]}
                        </Typography>
                    ))}
                </Box>
                <Box>
                    <Typography variant="subtitle1">
                        {getOfferLetterPayRateLabel(offerLetter)}
                    </Typography>
                </Box>
            </Box>
            <section className={classes.notes}>
                <FormSelect
                    className={classes.formInput}
                    name="rescissionReason"
                    label="Reason for rescind"
                    getKey={reason => reason.value}
                    getText={reason => reason.label}
                    options={RescissionReasons}
                    useIdValue
                />
                {hasCustomReason && (
                    <FormField
                        name="rescissionReasonText"
                        label="Reason for rescission"
                        className={clsx(classes.formInput, inputClasses.input)}
                        multiline
                        rows={2}
                        rowsMax={4}
                    />
                )}
            </section>
        </section>
    );
}
